import { createApi } from '@reduxjs/toolkit/query/react';

import { ApiResponse } from '../../model';
import { API_CONSTANTS } from '../../utils/constants';
import { PermissionListResponse } from '../roleManagement/permissions/model';

import type { IAllMenu, IParentMenu } from './model';
import { baseQueryWithReauth } from '../../apis/baseQuery';

export const dynamicMenuApi = createApi({
  reducerPath: 'dynamicMenuApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['dynamicMenuData', 'parentMenuAutocomplete'],

  endpoints: (build) => ({
    dynamicMenuList: build.query<ApiResponse<IAllMenu>, void>({
      query: () => ({
        url: API_CONSTANTS.DYNAMIC_MENU.LIST_URL,
        method: 'POST',
      }),
      providesTags: ['dynamicMenuData'],
    }),
    updateDynamicMenuSequence: build.mutation<ApiResponse<any>, any>({
      query: (body) => ({
        url: API_CONSTANTS.DYNAMIC_MENU.UPDATE_MENU_ORDER_URL,
        method: 'PUT',
        body,
      }),
    }),
    getPermissionList: build.query<ApiResponse<PermissionListResponse>, void>({
      query: () => ({
        url: API_CONSTANTS.PERMISSIONS.PERMISSIONS_LIST_URL,
        method: 'GET',
      }),
    }),
    getParentMenuAutoComplete: build.query<ApiResponse<IParentMenu>, void>({
      query: () => ({
        url: `${API_CONSTANTS.DYNAMIC_MENU.PARENT_MENU_AUTOCOMPLETE_URL}?type=Active`,
        method: 'GET',
      }),
      providesTags: ['parentMenuAutocomplete'],
    }),
    addMenu: build.mutation<
      ApiResponse<IAllMenu>,
      Partial<IAllMenu> & { redirectionAllow?: 'Yes' | 'No' }
    >({
      query: (body) => ({
        url: API_CONSTANTS.DYNAMIC_MENU.CREATE_URL,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['dynamicMenuData', 'parentMenuAutocomplete'],
    }),
    updateMenu: build.mutation<
      ApiResponse<IAllMenu>,
      { id: number; data: IAllMenu }
    >({
      query: ({ id, data }) => ({
        url: `${API_CONSTANTS.DYNAMIC_MENU.UPDATE_URL}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['dynamicMenuData', 'parentMenuAutocomplete'],
    }),
    deleteMenu: build.mutation<ApiResponse<unknown>, { id: number }>({
      query: ({ id }) => ({
        url: `${API_CONSTANTS.DYNAMIC_MENU.DELETE_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['dynamicMenuData', 'parentMenuAutocomplete'],
    }),
  }),
});

export const {
  useDynamicMenuListQuery,
  useUpdateDynamicMenuSequenceMutation,
  useGetPermissionListQuery,
  useGetParentMenuAutoCompleteQuery,
  useAddMenuMutation,
  useUpdateMenuMutation,
  useDeleteMenuMutation,
} = dynamicMenuApi;
